import { useRef, useEffect } from "react";
import { Gauge, FileSpreadsheet, Lightbulb, Search } from "lucide-react";
import AnimatedSection from "./AnimatedSection";

const Services = () => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      if (video.duration && video.currentTime >= video.duration - 2) {
        video.currentTime = 0;
        video.play();
      }
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    return () => video.removeEventListener("timeupdate", handleTimeUpdate);
  }, []);

  const benefits = [
    {
      icon: <Search className="h-6 w-6 text-sergen-600" />,
      title: "Análisis técnico avanzado",
      text: (
        <>
          Realizamos un análisis técnico detallado para identificar
          ineficiencias, pérdidas energéticas y puntos críticos. Entregamos un
          diagnóstico claro y accionable para optimizar tu consumo.
        </>
      ),
    },
    {
      icon: <Gauge className="h-6 w-6 text-sergen-600" />,
      title: "Predicción y alertas preventivas",
      text: (
        <>
          Anticipamos picos tarifarios mediante modelos predictivos y enviamos
          alertas oportunas que ayudan a evitar penalidades y reducir costos.
        </>
      ),
    },
    {
      icon: <FileSpreadsheet className="h-6 w-6 text-sergen-600" />,
      title: "Gestión tarifaria y reintegros",
      text: (
        <>
          Detectamos cobros indebidos y oportunidades de reintegro. Gestionamos
          el sustento técnico y regulatorio para lograr recuperaciones reales.
        </>
      ),
    },
    {
      icon: <Lightbulb className="h-6 w-6 text-sergen-600" />,
      title: "Monitoreo eléctrico integral",
      text: (
        <>
          Supervisamos tus variables eléctricas para identificar fallas,
          desviaciones y consumos inusuales, mejorando la eficiencia operativa.
        </>
      ),
    },
  ];

  return (
    <section id="services" className="bg-[#f3f1ed] py-20">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">

        {/* 🔵 TITULO GENERAL */}
        <AnimatedSection className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">
            Inteligencia energética aplicada a tu operación
          </h2>
          <p className="mt-2 mx-auto max-w-2xl text-base text-gray-700">
            Reduce costos energéticos con predicciones automáticas y datos en
            tiempo real.
          </p>
        </AnimatedSection>

        {/* 🔵 MOBILE */}
        <div className="md:hidden flex flex-col gap-6">
          <div className="w-full bg-white rounded-2xl shadow-xl overflow-hidden">
            <video
              ref={videoRef}
              src="/lovable-uploads/VIDEO3-sergen.mp4"
              className="w-full h-auto object-contain"
              autoPlay
              muted
              loop={false}
              playsInline
            />
          </div>

          <div className="flex flex-col gap-4">
            {benefits.map((b, i) => (
              <div
                key={i}
                className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm"
              >
                <div className="flex items-center mb-2">
                  {b.icon}
                  <h5 className="ml-3 text-[17px] font-semibold text-sergen-700">
                    {b.title}
                  </h5>
                </div>
                <p className="text-gray-700 text-[15px] leading-relaxed">
                  {b.text}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* 🔵 DESKTOP/TABLET VIEW */}
        <div className="hidden md:grid gap-12 content-center lg:grid-cols-[1.8fr_1.2fr]">

          {/* ⭐ VIDEO CENTRADO PERFECTAMENTE */}
          <div className="flex justify-start">
            <div className="w-full max-w-[760px] h-[540px] overflow-hidden rounded-3xl shadow-2xl">
              <video
                ref={videoRef}
                src="/lovable-uploads/VIDEO3-sergen.mp4"
                className="w-full h-full object-cover object-center"
                autoPlay
                muted
                loop={false}
                playsInline
              />
            </div>
          </div>

          {/* ⭐ CARDS */}
          <div className="flex flex-col gap-4 w-full">
            {benefits.map((b, i) => (
              <AnimatedSection
                key={i}
                delay={i * 0.15}
                className="bg-white border border-gray-200 rounded-xl py-4 px-6 hover:shadow-lg hover:shadow-gray-300/40 transition-all duration-300"
              >
                <div className="flex items-center mb-2">
                  {b.icon}
                  <h5 className="ml-3 text-[17px] font-semibold text-sergen-700">
                    {b.title}
                  </h5>
                </div>
                <p className="text-gray-700 text-[15px] leading-relaxed">
                  {b.text}
                </p>
              </AnimatedSection>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
};

export default Services;